import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotEl = form.querySelector<HTMLInputElement>('[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Vielen Dank! Du hast dich erfolgreich angemeldet.');
      setEmail('');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value.toString());
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9b5jqdv8hat0f6uqjeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Vielen Dank! Du hast dich erfolgreich angemeldet.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Netzwerkfehler. Bitte überprüfe deine Verbindung.');
    }

    setTimeout(() => setFormStatus('idle'), 4000);
  };

  return (
    <footer className="bg-background-900 text-background-100 rounded-t-3xl mx-4 md:mx-8 mt-16">
      <div className="px-6 md:px-12 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Left - Subscribe */}
          <div className="md:col-span-5">
            <h3 className="font-heading text-4xl md:text-5xl font-light text-background-50 mb-2">
              OOPBUY Updates
            </h3>
            <p className="text-sm text-background-400 leading-relaxed mb-8 max-w-sm">
              Erhalte neue Spreadsheet Updates, Produktempfehlungen und exklusive Tipps direkt in dein Postfach.
            </p>
            <form onSubmit={handleSubmit} className="relative" data-readdy-form="">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Deine E-Mail Adresse"
                required
                className="w-full max-w-sm bg-transparent border-b border-background-600 py-3 text-sm text-background-100 placeholder:text-background-500 outline-none focus:border-background-300 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-protection-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="mt-4 px-6 py-2.5 bg-background-50 text-foreground-900 rounded-full text-sm font-semibold flex items-center gap-2 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Abonnieren
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </button>
              {formStatus === 'success' && (
                <p className="mt-2 text-xs text-primary-300">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-2 text-xs text-accent-400">{formMessage}</p>
              )}
            </form>
          </div>

          {/* Middle - Navigation */}
          <div className="md:col-span-3">
            <p className="text-xs font-semibold text-background-500 uppercase tracking-wider mb-5">Navigation</p>
            <div className="flex flex-col gap-3">
              <a href="#kategorien" className="text-sm text-background-300 hover:text-background-100 transition-colors underline decoration-background-600 underline-offset-4">Kategorien</a>
              <a href="#guides" className="text-sm text-background-300 hover:text-background-100 transition-colors underline decoration-background-600 underline-offset-4">Guides</a>
              <Link to="/artikel/oopbuy-spreadsheet-guide" className="text-sm text-background-300 hover:text-background-100 transition-colors underline decoration-background-600 underline-offset-4">Blog</Link>
              <a href="#faq" className="text-sm text-background-300 hover:text-background-100 transition-colors underline decoration-background-600 underline-offset-4">FAQ</a>
            </div>
          </div>

          {/* Right - Contact & Main Site */}
          <div className="md:col-span-4">
            <p className="text-xs font-semibold text-background-500 uppercase tracking-wider mb-5">Kontakt &amp; Hauptseite</p>
            <div className="flex flex-col gap-2 mb-6">
              <p className="text-sm text-background-300">kontakt@oopbuyspread.study</p>
            </div>
            <p className="text-xs font-semibold text-background-500 uppercase tracking-wider mb-3">Unsere Hauptwebsite</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-background-200 hover:text-background-50 transition-colors underline decoration-background-600 underline-offset-4"
            >
              kaspreadsheet.com
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-background-700/50 mx-6 md:mx-12"></div>
      <div className="px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <a href="#" className="text-xs text-background-500 hover:text-background-300 transition-colors underline decoration-background-700 underline-offset-4">Instagram</a>
          <a href="#" className="text-xs text-background-500 hover:text-background-300 transition-colors underline decoration-background-700 underline-offset-4">Pinterest</a>
          <a href="#" className="text-xs text-background-500 hover:text-background-300 transition-colors underline decoration-background-700 underline-offset-4">TikTok</a>
        </div>
        <p className="text-xs text-background-500 text-center">
          &copy; {new Date().getFullYear()} oopbuyspread.study &ndash; Alle Rechte vorbehalten.
        </p>
        <div className="flex items-center gap-6">
          <a href="#" className="text-xs text-background-500 hover:text-background-300 transition-colors underline decoration-background-700 underline-offset-4">Datenschutz</a>
          <a href="#" className="text-xs text-background-500 hover:text-background-300 transition-colors underline decoration-background-700 underline-offset-4">Impressum</a>
        </div>
      </div>
    </footer>
  );
}